<template>
  <div class="oc-wizard-nav">
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-75">
      <div class="oc-wizard-nav__current">
        <span class="oc-wizard-nav__step-label">Step {{ currentStep + 1 }} of {{ steps.length }}</span>
        <strong class="oc-wizard-nav__step-name">{{ steps[currentStep] }}</strong>
      </div>
      <div class="d-flex align-items-center oc-wizard-nav__progress-wrap">
        <span class="oc-wizard-nav__pct d-none d-sm-inline">{{ completionPercent }}% complete</span>
        <b-progress
          :value="completionPercent"
          max="100"
          height="8px"
          variant="primary"
          class="oc-wizard-nav__progress"
        />
      </div>
    </div>

    <div class="oc-wizard-nav__track">
      <b-button
        v-for="(label, idx) in steps"
        :key="label"
        size="sm"
        :variant="stepVariant(idx)"
        class="oc-wizard-nav__btn"
        :class="{
          'oc-wizard-nav__btn--active': idx === currentStep,
          'oc-wizard-nav__btn--done': idx < currentStep,
        }"
        :disabled="!canGoTo(idx)"
        @click="$emit('select', idx)"
      >
        <span class="oc-wizard-nav__badge" :class="badgeClass(idx)">
          <feather-icon
            v-if="idx < currentStep"
            icon="CheckIcon"
            size="11"
          />
          <span v-else>{{ idx + 1 }}</span>
        </span>
        <span class="oc-wizard-nav__btn-label d-none d-md-inline">{{ label }}</span>
        <span class="oc-wizard-nav__btn-label d-inline d-md-none">{{ shortLabel(label) }}</span>
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BButton,
  BProgress,
} from 'bootstrap-vue'

const SHORT_LABELS = {
  'Site & Role': 'Site',
  'Write Letter': 'Letter',
  'Salary & Branding': 'Salary',
  'Review & Save': 'Review',
}

export default {
  name: 'OfferConfigureWizardNav',
  components: {
    BButton,
    BProgress,
  },
  props: {
    steps: {
      type: Array,
      default: () => [],
    },
    currentStep: {
      type: Number,
      default: 0,
    },
    completionPercent: {
      type: Number,
      default: 0,
    },
    canGoTo: {
      type: Function,
      required: true,
    },
  },
  methods: {
    stepVariant(idx) {
      if (idx === this.currentStep) return 'primary'
      if (idx < this.currentStep) return 'outline-primary'
      return 'outline-secondary'
    },
    badgeClass(idx) {
      if (idx === this.currentStep) return 'oc-wizard-nav__badge--active'
      if (idx < this.currentStep) return 'oc-wizard-nav__badge--done'
      return 'oc-wizard-nav__badge--pending'
    },
    shortLabel(label) {
      return SHORT_LABELS[label] || label.split(' ')[0]
    },
  },
}
</script>

<style scoped>
.oc-wizard-nav__current {
  line-height: 1.3;
}

.oc-wizard-nav__step-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6e6b7b;
}

.oc-wizard-nav__step-name {
  font-size: 0.9375rem;
  color: #00bab0;
}

.oc-wizard-nav__progress-wrap {
  gap: 0.5rem;
}

.oc-wizard-nav__pct {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6e6b7b;
  white-space: nowrap;
}

.oc-wizard-nav__progress {
  width: 6rem;
  min-width: 5rem;
  border-radius: 1rem;
  background: #eef0f3;
}

.oc-wizard-nav__track {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.4rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

.oc-wizard-nav__btn {
  flex: 1 1 auto;
  min-width: fit-content;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.45rem 0.65rem;
  border-radius: 0.4rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  transition: all 0.15s ease;
}

/* Active step — ensure visible contrast (fixes white-on-white) */
.oc-wizard-nav__btn--active.btn-primary {
  background-color: #00bab0 !important;
  border-color: #00bab0 !important;
  color: #fff !important;
  box-shadow: 0 2px 6px rgba(0, 186, 176, 0.35);
}

.oc-wizard-nav__btn--active .oc-wizard-nav__badge--active {
  background: #fff;
  color: #00bab0;
}

.oc-wizard-nav__btn--done.btn-outline-primary {
  border-color: rgba(0, 186, 176, 0.5);
  color: #0f766e;
}

.oc-wizard-nav__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.35rem;
  height: 1.35rem;
  border-radius: 50%;
  font-size: 0.625rem;
  font-weight: 700;
  flex-shrink: 0;
}

.oc-wizard-nav__badge--active {
  background: #fff;
  color: #00bab0;
}

.oc-wizard-nav__badge--done {
  background: #00bab0;
  color: #fff;
}

.oc-wizard-nav__badge--pending {
  background: #e5e7eb;
  color: #6b7280;
}

.oc-wizard-nav__btn-label {
  line-height: 1.2;
}

@media (max-width: 767.98px) {
  .oc-wizard-nav__btn {
    flex: 0 0 auto;
    padding: 0.4rem 0.55rem;
  }
}
</style>
